/**
 * Chart hover tooltips (KeyLevelTooltip): what to show after a crosshair
 * move or a click, so a marker's tooltip stays put.
 *
 * Why this exists: lightweight-charts re-fires the crosshair-move event on
 * every live data update (no source event, the last pointer position), and
 * the main and volume charts sync their crosshairs. Treating those re-fires
 * like pointer moves made a marker's tooltip blink off on every tick.
 *
 * Rules:
 * - A re-fire (live update, not the pointer) never hides or swaps a marker
 *   tooltip: the pointer has not moved.
 * - Leaving a marker hides its tooltip after MARKER_HOVER_GRACE_MS, so a
 *   one-pixel slip off the ring does not blink it; coming back cancels that.
 * - Touch: a tap on a marker pins its tooltip (crosshair moves leave it
 *   alone); tapping it again, or anywhere else, closes it.
 */
export const MARKER_HOVER_GRACE_MS = 150;

/** Hover ids of point markers (order-flow, absorption, Vest spikes), not lines or zones. */
export function isMarkerHoverId(id: string | null | undefined): boolean {
  return !!id && (id.startsWith('flow:') || id.startsWith('abs:') || id.startsWith('spike:'));
}

export interface HoverEvent {
  kind: 'move' | 'click';
  /** From the pointer (or the pointer leaving); false for a live-update re-fire. */
  fromPointer: boolean;
  /** A tap on a touch screen. */
  touch: boolean;
}

export interface HoverDecision<T> {
  show: T | null;
  pinned: boolean;
  /** Keep `show` for now, but hide it after the grace period unless the pointer comes back. */
  graceClear: boolean;
}

export function decideHover<T extends { hoverId: string }>(
  prev: T | null,
  pinned: boolean,
  next: T | null,
  ev: HoverEvent,
): HoverDecision<T> {
  const same = (a: T | null, b: T | null) => (a?.hoverId ?? null) === (b?.hoverId ?? null);
  if (ev.kind === 'click' && ev.touch) {
    if (next && isMarkerHoverId(next.hoverId)) {
      // Tap the pinned marker again: close.
      if (pinned && same(prev, next)) return { show: null, pinned: false, graceClear: false };
      return { show: next, pinned: true, graceClear: false };
    }
    // Tap anywhere else: a pinned tooltip closes; a level / zone shows as before.
    return { show: next, pinned: false, graceClear: false };
  }
  if (pinned) return { show: prev, pinned: true, graceClear: false };
  if (same(prev, next)) return { show: prev, pinned: false, graceClear: false };
  if (!ev.fromPointer) {
    // The pointer did not move: never hide, and never swap a marker tooltip.
    if (next === null || (prev && isMarkerHoverId(prev.hoverId))) return { show: prev, pinned: false, graceClear: false };
    return { show: next, pinned: false, graceClear: false };
  }
  if (next === null && prev && isMarkerHoverId(prev.hoverId)) return { show: prev, pinned: false, graceClear: true };
  return { show: next, pinned: false, graceClear: false };
}

/**
 * Whether a crosshair event came from the pointer: lightweight-charts passes
 * the DOM event for pointer moves and nothing for its own re-fires (data
 * updates, synthetic positions); the pointer leaving has no point at all.
 */
export function isPointerEvent(param: { sourceEvent?: unknown; point?: unknown }): boolean {
  return param.sourceEvent !== undefined && param.sourceEvent !== null ? true : !param.point;
}

/** A touch screen (taps pin a marker's tooltip). */
export function isCoarsePointer(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}
